import { useCallback, VFC } from 'react';

import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import FieldMappingsPanel from 'dashboard/components/WorkflowControlPanel/StepConfigAction/FieldMappingsPanel';
import FieldsList from 'dashboard/components/WorkflowControlPanel/StepConfigAction/FieldsList';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import { getTokensToDisplayValue, UsableObjectConfig } from 'dashboard/lib/workflows';
import { Verification } from 'dashboard/models/verification';
import { WorkflowError } from 'dashboard/models/workflow';
import { WorkflowActionRunVerificationGenericV1 } from 'dashboard/models/workflow-version';
import Spacer from 'design-system/components/Spacer';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';

interface RunVerificationConfigMappingProps {
  editable: boolean;
  usableObjects: UsableObjectConfig[];
  errors: WorkflowError[];
  verificationMeta: WorkflowActionRunVerificationGenericV1['params']['verification-meta'];
  verificationType: WorkflowActionRunVerificationGenericV1['params']['verification-type'];
  updateVerificationMeta: (
    newParam: WorkflowActionRunVerificationGenericV1['params']['verification-meta'],
  ) => void;
  getCurrentCompAttr: (
    verificationType: Verification['type'],
  ) => Record<string, string> | null | undefined;
}

const RunVerificationConfigMapping: VFC<RunVerificationConfigMappingProps> = ({
  editable,
  usableObjects,
  errors,
  verificationMeta,
  verificationType,
  updateVerificationMeta,
  getCurrentCompAttr,
}) => {
  const editComparisonAttributes = useCallback(
    (comparisonAttributes: Record<string, string>) => {
      switch (verificationType) {
        case ApiModel.VerificationDocument: {
          return updateVerificationMeta({
            document: {
              'comparison-attributes': {
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                ...verificationMeta['document']?.['comparison-attributes'],
                ...comparisonAttributes,
              },
            },
          });
        }
        case ApiModel.VerificationDatabase: {
          return updateVerificationMeta({
            database: {
              'comparison-attributes': {
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                ...verificationMeta['database']?.['comparison-attributes'],
                ...comparisonAttributes,
              },
            },
          });
        }
        case ApiModel.VerificationGovernmentId: {
          return updateVerificationMeta({
            'government-id': {
              'comparison-attributes': {
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                ...verificationMeta['government-id']?.['comparison-attributes'],
                ...comparisonAttributes,
              },
            },
          });
        }
        case ApiModel.VerificationSelfie: {
          return updateVerificationMeta({
            selfie: {
              'comparison-attributes': {
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                ...verificationMeta['selfie']?.['comparison-attributes'],
                ...comparisonAttributes,
              },
            },
          });
        }
      }
    },
    [verificationMeta, verificationType, updateVerificationMeta],
  );

  const { renderErrorMessageFor } = useWorkflowError(errors);

  const [showEditPanel, { toggle: toggleEditPanel, disable: closeEditPanel }] = useToggle();
  return (
    <>
      <Spacer height={2} />
      <ParamLabel
        labelText='Comparison Attributes'
        htmlFor='params-run-verification-generic__comparison_attributes'
      />
      {showEditPanel && (
        <FieldMappingsPanel
          title='Comparison Attributes'
          usableObjects={usableObjects}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          fieldMappings={getCurrentCompAttr(verificationType)}
          onOutsideClick={closeEditPanel}
          onUpdateFieldMappings={editComparisonAttributes}
          removable={false}
        />
      )}
      {renderErrorMessageFor('verification-meta')}
      <Spacer height={1} />
      <FieldsList
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        fields={getCurrentCompAttr(verificationType)}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        fieldKey={`verification-meta.${verificationType.split('/')[1]}.comparison-attributes`}
        editable={editable}
        toggleEditPanel={toggleEditPanel}
        tokensToDisplayValue={getTokensToDisplayValue(usableObjects)}
        errors={errors}
      />
    </>
  );
};
export default RunVerificationConfigMapping;
